import React, { useEffect, useMemo, useState } from "react";
import { Alert, Checkbox, DropDownNative, Input, Modal } from "@tag/tag-components-react-v4";
import { ProjectAccents } from "../../General/config/projectAccents";
import SurveyJSTemplateService from "../services/SurveyJSTemplateService";
import { SurveyJSTemplateDto, SurveyJSTemplateTypeDto } from "../types/SurveyJSTemplate";
import styles from "./SurveyJSTemplates.module.scss";

interface Props {
    visible: boolean;
    types: SurveyJSTemplateTypeDto[];
    onUploaded: (template: SurveyJSTemplateDto) => void;
    onCancel: () => void;
}

const PLACEHOLDER_TYPE = "__placeholder__";

function UploadTemplateDialog({ visible, types, onUploaded, onCancel }: Props) {
    const hasExistingTypes = types.length > 0;

    // Mode: "newVersion" picks an existing type; "newType" defines a brand-new slot.
    const [isNewType, setIsNewType] = useState<boolean>(!hasExistingTypes);

    // Common fields
    const [templateName, setTemplateName] = useState("");
    const [version, setVersion] = useState("");
    const [file, setFile] = useState<File | undefined>();

    // "New version" mode
    const [selectedTypeKey, setSelectedTypeKey] = useState<string>(PLACEHOLDER_TYPE);
    const selectedType = useMemo(
        () => types.find(t => t.typeKey === selectedTypeKey),
        [types, selectedTypeKey]
    );

    // "New distinct template" mode
    const [newTypeModule, setNewTypeModule] = useState<string>("");
    const [newTypeDisplayName, setNewTypeDisplayName] = useState("");
    const [newTypeFeatureName, setNewTypeFeatureName] = useState("");
    const [newTypeCodeSuffix, setNewTypeCodeSuffix] = useState("");
    const [newTypeDescription, setNewTypeDescription] = useState("");

    const [error, setError] = useState<string | undefined>();
    const [busy, setBusy] = useState(false);

    // Distinct modules from existing types
    const moduleOptions = useMemo(() => {
        const byKey = new Map<string, string>();
        for (const t of types) {
            if (!byKey.has(t.module)) byKey.set(t.module, t.moduleDisplayName);
        }
        const items = Array.from(byKey.entries()).map(([key, display]) => ({
            label: display,
            value: key,
        }));
        return [{ label: "Select a module", value: "" }, ...items];
    }, [types]);

    useEffect(() => {
        // When dialog opens, recompute the default mode and clear state
        if (!visible) return;
        setIsNewType(!hasExistingTypes);
        setSelectedTypeKey(PLACEHOLDER_TYPE);
        setTemplateName("");
        setVersion("");
        setFile(undefined);
        setNewTypeModule(types[0]?.module ?? "");
        setNewTypeDisplayName("");
        setNewTypeFeatureName("");
        setNewTypeCodeSuffix("");
        setNewTypeDescription("");
        setError(undefined);
        setBusy(false);
    }, [visible, hasExistingTypes, types]);

    // Auto-fill template name from the chosen type's default when in "new version" mode
    useEffect(() => {
        if (isNewType) return;
        const t = types.find(x => x.typeKey === selectedTypeKey);
        if (t && templateName.trim().length === 0) {
            setTemplateName(t.defaultTemplateName);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTypeKey, isNewType]);

    const existingTypeDropdownData = useMemo(() => {
        const items = types
            .filter(t => !t.isRetired)
            .map(t => ({
                label: `${t.moduleDisplayName} / ${t.typeDisplayName}`,
                value: t.typeKey,
            }));
        return [{ label: "Select a template", value: PLACEHOLDER_TYPE }, ...items];
    }, [types]);

    const moduleDisplayNameForChosen = useMemo(() => {
        return types.find(t => t.module === newTypeModule)?.moduleDisplayName ?? "";
    }, [types, newTypeModule]);

    const handleCancel = () => {
        onCancel();
    };

    const handleSubmit = async () => {
        setError(undefined);

        if (!templateName.trim()) { setError("Template name is required."); return; }
        if (!version.trim()) { setError("Version is required."); return; }
        if (!file) { setError("Please choose a JSON file to upload."); return; }

        if (!isNewType) {
            if (!hasExistingTypes) { setError("No existing templates to version. Tick \"new distinct template\"."); return; }
            if (selectedTypeKey === PLACEHOLDER_TYPE) { setError("Please choose which template you're uploading a new version of."); return; }
        } else {
            if (!newTypeModule.trim()) { setError("Module is required for a new template."); return; }
            if (!newTypeDisplayName.trim()) { setError("Template type name is required."); return; }
            if (!newTypeFeatureName.trim()) { setError("Feature name is required."); return; }
            if (!newTypeCodeSuffix.trim()) { setError("Code suffix is required."); return; }
        }

        setBusy(true);
        try {
            const uploaded = await SurveyJSTemplateService.uploadTemplate(
                isNewType
                    ? {
                        templateName: templateName.trim(),
                        version: version.trim(),
                        file,
                        isNewType: true,
                        newTypeModule: newTypeModule.trim(),
                        newTypeModuleDisplayName: moduleDisplayNameForChosen.trim() || newTypeModule.trim(),
                        newTypeDisplayName: newTypeDisplayName.trim(),
                        newTypeFeatureName: newTypeFeatureName.trim(),
                        newTypeCodeSuffix: newTypeCodeSuffix.trim(),
                        newTypeDescription: newTypeDescription.trim(),
                    }
                    : {
                        templateName: templateName.trim(),
                        version: version.trim(),
                        file,
                        isNewType: false,
                        templateType: selectedTypeKey,
                    }
            );
            onUploaded(uploaded);
        } catch (e: any) {
            setError(e?.message || "Upload failed.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            visible={visible}
            accent={ProjectAccents.CareCompliance}
            heading="Upload SurveyJS template"
            primaryButtonProps={{ text: busy ? "Uploading…" : "Upload", onClick: handleSubmit, disabled: busy }}
            cancelButtonProps={{ text: "Cancel", onClick: handleCancel, disabled: busy }}
        >
            <div className={styles.ModalContent}>
                {error ? <Alert type="error">{error}</Alert> : null}

                <div className={styles.ConfirmCheckboxRow}>
                    <Checkbox
                        checked={isNewType}
                        onChange={(value: any) => setIsNewType(!!value.checked)}
                        disabled={busy || !hasExistingTypes}
                    />
                    <span>
                        This is a new distinct template — not a new version of an existing one
                        {!hasExistingTypes ? (
                            <span className={styles.SubLabel}> (forced on — no existing templates to version yet)</span>
                        ) : null}
                    </span>
                </div>

                {!isNewType ? (
                    <div className={styles.FormField}>
                        <label>Template to update (new version)</label>
                        <DropDownNative
                            value={selectedTypeKey}
                            data={existingTypeDropdownData}
                            onChange={(e: any) => setSelectedTypeKey(e.target.value)}
                            disabled={busy}
                            style={{ width: "100%" }}
                        />
                        {selectedType ? (
                            <span className={styles.SubLabel}>
                                Module: <b>{selectedType.moduleDisplayName}</b> · Feature: {selectedType.featureName} · Code suffix: -{selectedType.codeSuffix}
                            </span>
                        ) : null}
                    </div>
                ) : (
                    <>
                        <div className={styles.FormField}>
                            <label>Module</label>
                            <DropDownNative
                                value={newTypeModule}
                                data={moduleOptions}
                                onChange={(e: any) => setNewTypeModule(e.target.value)}
                                disabled={busy}
                                style={{ width: "100%" }}
                            />
                            <span className={styles.SubLabel}>
                                Pick the module this new template belongs under. (To add a brand-new module, talk to an admin.)
                            </span>
                        </div>
                        <div className={styles.FormField}>
                            <label>Template type name</label>
                            <Input
                                value={newTypeDisplayName}
                                onChange={(e) => setNewTypeDisplayName((e.target as HTMLInputElement).value)}
                                placeholder="e.g. Risk Assessment Form"
                                disabled={busy}
                            />
                        </div>
                        <div className={styles.FormField}>
                            <label>Feature name (sent to Care Compliance backend)</label>
                            <Input
                                value={newTypeFeatureName}
                                onChange={(e) => setNewTypeFeatureName((e.target as HTMLInputElement).value)}
                                placeholder="e.g. risk-assessment-management"
                                disabled={busy}
                            />
                        </div>
                        <div className={styles.FormField}>
                            <label>Code suffix (the &quot;{`{clientId}-X`}&quot; suffix)</label>
                            <Input
                                value={newTypeCodeSuffix}
                                onChange={(e) => setNewTypeCodeSuffix((e.target as HTMLInputElement).value)}
                                placeholder="e.g. 2"
                                disabled={busy}
                            />
                        </div>
                        <div className={styles.FormField}>
                            <label>Description (optional)</label>
                            <Input
                                value={newTypeDescription}
                                onChange={(e) => setNewTypeDescription((e.target as HTMLInputElement).value)}
                                placeholder="Short description sent on the Care Compliance template payload"
                                disabled={busy}
                            />
                        </div>
                    </>
                )}

                <div className={styles.FormField}>
                    <label>Template name</label>
                    <Input
                        value={templateName}
                        onChange={(e) => setTemplateName((e.target as HTMLInputElement).value)}
                        placeholder={!isNewType && selectedType ? selectedType.defaultTemplateName : "e.g. Master Template"}
                        disabled={busy}
                    />
                </div>

                <div className={styles.FormField}>
                    <label>Version (integer)</label>
                    <Input
                        value={version}
                        onChange={(e) => setVersion((e.target as HTMLInputElement).value)}
                        placeholder="e.g. 1"
                        disabled={busy}
                    />
                </div>

                <div className={styles.FormField}>
                    <label>JSON template file</label>
                    <input
                        className={styles.FileInput}
                        type="file"
                        accept="application/json,.json"
                        onChange={(e) => setFile(e.target.files?.[0])}
                        disabled={busy}
                    />
                </div>
            </div>
        </Modal>
    );
}

export default UploadTemplateDialog;
